import { useEffect, useState } from 'react';
import type { CardData, Suit } from '../game/types';
import { Card } from './Board';
import { DeckEditor } from './DeckEditor';
import { useDialog } from './Dialog';
import './Board.css';

import { SERVER_ORIGIN } from '../config';

interface UserCardRecord {
  id: string;
  card: CardData;
  acquiredAt: string;
  source: string;
}

interface UserDeckRecord {
  id: string;
  name: string;
  createdAt: string;
  cards: UserCardRecord[];
}

const SUIT_ORDER: Suit[] = ['drug', 'money', 'cars', 'police', 'jewels', 'casino chips', 'criminal contacts', 'weapons'];

const AVATAR_COUNT = 12; // mirrors public/avatars.png (4×3 grid)
const MAX_DECKS = 10;    // keep in sync with MAX_DECKS_PER_USER in server.ts

function deckBtnStyle(bg: string, color: string): React.CSSProperties {
  return {
    background: bg,
    border: `1px solid ${color}`,
    color,
    padding: '5px 10px',
    borderRadius: '4px',
    fontSize: '11px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
}

interface CollectionProps {
  token: string;
  currentAvatarId: number | null;
  onAvatarChange: (id: number | null) => void;
  // Suit → lifetime count, banked from successful run extracts. The display
  // skips suits whose count is 0 so the section stays clean for new accounts.
  bankedResources: Record<string, number>;
  stats?: {
    matchesPlayed: number;
    matchesWon: number;
    matchesLost: number;
    matchesDrawn: number;
  };
}

export function Collection({ token, currentAvatarId, onAvatarChange, bankedResources, stats }: CollectionProps) {
  const dialog = useDialog();
  const [cards, setCards] = useState<UserCardRecord[]>([]);
  const [decks, setDecks] = useState<UserDeckRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [editorState, setEditorState] = useState<
    | { open: false }
    | { open: true; mode: 'create' }
    | { open: true; mode: 'edit'; deck: UserDeckRecord }
  >({ open: false });
  const [deckActionError, setDeckActionError] = useState<string | null>(null);
  const [pendingDeckId, setPendingDeckId] = useState<string | null>(null);

  const [newLoot, setNewLoot] = useState<{
    cardIds: string[];
    resources: Record<string, number>;
  } | null>(null);
  const [showHighlights, setShowHighlights] = useState(false);

  useEffect(() => {
    const lootStr = localStorage.getItem('gs_new_loot');
    if (lootStr) {
      try {
        const parsed = JSON.parse(lootStr);
        if (parsed && ((parsed.cardIds && parsed.cardIds.length > 0) || (parsed.resources && Object.keys(parsed.resources).length > 0))) {
          setNewLoot(parsed);
          setShowHighlights(true);
          const timer = setTimeout(() => {
            setShowHighlights(false);
            localStorage.removeItem('gs_new_loot');
            window.dispatchEvent(new CustomEvent('gs-new-loot-updated'));
          }, 5000);
          return () => {
            clearTimeout(timer);
            localStorage.removeItem('gs_new_loot');
            window.dispatchEvent(new CustomEvent('gs-new-loot-updated'));
          };
        }
      } catch (e) {
        // ignore
      }
    }
  }, []);

  // ⚠️ DEV ONLY — REMOVE BEFORE RELEASE. One-click "grant a full card set".
  const [grantingAll, setGrantingAll] = useState(false);
  const grantAllCards = async () => {
    if (grantingAll) return;
    setGrantingAll(true);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/dev/grant-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        await dialog.alert({ title: 'Grant failed', message: data.error ?? 'unknown error', variant: 'error' });
        return;
      }
      // Re-fetch the collection so the new cards appear immediately.
      const cardsRes = await fetch(`${SERVER_ORIGIN}/auth/cards`, { headers: { Authorization: `Bearer ${token}` } });
      const cardsJson = await cardsRes.json();
      if (cardsRes.ok) setCards(cardsJson.cards as UserCardRecord[]);
      await dialog.alert({ title: 'Cards added', message: `Added ${data.granted} cards to your collection.` });
    } catch (e) {
      await dialog.alert({ title: 'Error', message: e instanceof Error ? e.message : 'network error', variant: 'error' });
    } finally {
      setGrantingAll(false);
    }
  };

  const refreshDecks = async () => {
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/decks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (res.ok) setDecks(json.decks as UserDeckRecord[]);
    } catch { /* ignored — refresh is best-effort */ }
  };

  const duplicateDeck = async (deck: UserDeckRecord) => {
    if (pendingDeckId) return;
    setPendingDeckId(deck.id);
    setDeckActionError(null);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/decks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          name: `${deck.name} (copy)`.slice(0, 64),
          cardIds: deck.cards.map(c => c.id),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setDeckActionError(data.error ?? 'duplicate failed'); return; }
      await refreshDecks();
    } catch (e) {
      setDeckActionError(e instanceof Error ? e.message : 'network error');
    } finally {
      setPendingDeckId(null);
    }
  };

  const deleteDeck = async (deck: UserDeckRecord) => {
    if (pendingDeckId) return;
    const ok = await dialog.confirm({
      title: 'Delete deck?',
      message: `You're about to delete "${deck.name}".\nThis action cannot be undone.`,
      okLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    setPendingDeckId(deck.id);
    setDeckActionError(null);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/decks/${deck.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) { setDeckActionError(data.error ?? 'deletion failed'); return; }
      await refreshDecks();
    } catch (e) {
      setDeckActionError(e instanceof Error ? e.message : 'network error');
    } finally {
      setPendingDeckId(null);
    }
  };

  const pickAvatar = async (id: number) => {
    if (savingAvatar) return;
    setSavingAvatar(true);
    setAvatarError(null);
    try {
      const res = await fetch(`${SERVER_ORIGIN}/auth/avatar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ avatarId: id }),
      });
      const data = await res.json();
      if (!res.ok) {
        setAvatarError(data.error ?? 'failed to save avatar');
        return;
      }
      onAvatarChange(id);
    } catch (e) {
      setAvatarError(e instanceof Error ? e.message : 'network error');
    } finally {
      setSavingAvatar(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      try {
        const [cardsRes, decksRes] = await Promise.all([
          fetch(`${SERVER_ORIGIN}/auth/cards`, { headers }),
          fetch(`${SERVER_ORIGIN}/auth/decks`, { headers }),
        ]);
        const cardsJson = await cardsRes.json();
        const decksJson = await decksRes.json();
        if (cancelled) return;
        if (!cardsRes.ok) {
          setError(cardsJson.error ?? 'failed to load cards');
          return;
        }
        if (!decksRes.ok) {
          setError(decksJson.error ?? 'failed to load decks');
          return;
        }
        setCards(cardsJson.cards as UserCardRecord[]);
        setDecks(decksJson.decks as UserDeckRecord[]);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return <div style={{ padding: '40px', color: '#888' }}>Loading collection...</div>;
  }
  if (error) {
    return <div style={{ padding: '40px', color: '#e57373' }}>Error: {error}</div>;
  }
  if (cards.length === 0) {
    return <div style={{ padding: '40px', color: '#888' }}>Your collection is empty.</div>;
  }

  // Group cards by suit, sort by type (Hand before Mission) then value.
  const bySuit = new Map<Suit, UserCardRecord[]>();
  for (const r of cards) {
    const arr = bySuit.get(r.card.suit) ?? [];
    arr.push(r);
    bySuit.set(r.card.suit, arr);
  }
  for (const arr of bySuit.values()) {
    arr.sort((a, b) => {
      if (a.card.type !== b.card.type) return a.card.type === 'Hand' ? -1 : 1;
      return a.card.value - b.card.value;
    });
  }

  return (
    <div style={{
      padding: '20px 30px', overflowY: 'auto',
      // box-sizing border-box is the critical bit: without it, height:100% +
      // padding makes this container ~40px taller than its flex parent and
      // its bottom slips behind the LegalFooter. Lobby doesn't show the bug
      // because its internal panels use flex sizing instead of height:100%.
      height: '100%', boxSizing: 'border-box',
      background: 'var(--bg-color)',
    }}>
      {/* Avatar picker */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ color: 'var(--accent-color)', marginTop: 0, marginBottom: '4px' }}>Your Character</h2>
        <p style={{ color: '#888', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
          Pick an avatar — it's visible to you and your opponents during matches.
        </p>
        {avatarError && (
          <div style={{ color: '#e57373', fontSize: '12px', marginBottom: '10px' }}>{avatarError}</div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {Array.from({ length: AVATAR_COUNT }, (_, i) => i).map(i => {
            const selected = currentAvatarId === i;
            return (
              <button
                key={i}
                onClick={() => pickAvatar(i)}
                disabled={savingAvatar}
                title={`Avatar #${i + 1}`}
                style={{
                  width: '72px',
                  height: '72px',
                  padding: 0,
                  borderRadius: '8px',
                  border: selected ? '3px solid var(--accent-color)' : '2px solid #444',
                  overflow: 'hidden',
                  cursor: savingAvatar ? 'wait' : 'pointer',
                  background: '#1a1a24',
                  boxShadow: selected ? '0 0 8px var(--accent-color)' : 'none',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
              >
                <div
                  className={`avatar-sprite avatar-sprite-${i}`}
                  style={{ width: '100%', height: '100%' }}
                />
              </button>
            );
          })}
        </div>
      </section>

      {/* Stash — lifetime resources from successful extracts. */}
      <Stash bankedResources={bankedResources} newLoot={showHighlights ? newLoot : null} />

      {/* Stats — player stats from completed matches. */}
      {stats && (
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ color: 'var(--accent-color)', marginTop: 0, marginBottom: '4px' }}>Your Stats</h2>
          <p style={{ color: '#888', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
            Your lifetime match records and win rates.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{
              flex: '1 1 120px', padding: '12px 16px', borderRadius: '6px',
              background: '#15151f', border: '1px solid #2a2a3a', textAlign: 'center'
            }}>
              <div style={{ color: '#aaa', fontSize: '9px', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>Played</div>
              <div style={{ color: '#eee', fontSize: '20px', fontWeight: 'bold' }}>{stats.matchesPlayed}</div>
            </div>
            <div style={{
              flex: '1 1 120px', padding: '12px 16px', borderRadius: '6px',
              background: '#15151f', border: '1px solid #2a2a3a', textAlign: 'center'
            }}>
              <div style={{ color: '#4CAF50', fontSize: '9px', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>Won</div>
              <div style={{ color: '#eee', fontSize: '20px', fontWeight: 'bold' }}>{stats.matchesWon}</div>
            </div>
            <div style={{
              flex: '1 1 120px', padding: '12px 16px', borderRadius: '6px',
              background: '#15151f', border: '1px solid #2a2a3a', textAlign: 'center'
            }}>
              <div style={{ color: '#e57373', fontSize: '9px', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>Lost</div>
              <div style={{ color: '#eee', fontSize: '20px', fontWeight: 'bold' }}>{stats.matchesLost}</div>
            </div>
            <div style={{
              flex: '1 1 120px', padding: '12px 16px', borderRadius: '6px',
              background: '#15151f', border: '1px solid #2a2a3a', textAlign: 'center'
            }}>
              <div style={{ color: '#ffb703', fontSize: '9px', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>Drawn</div>
              <div style={{ color: '#eee', fontSize: '20px', fontWeight: 'bold' }}>{stats.matchesDrawn}</div>
            </div>
            <div style={{
              flex: '1 1 120px', padding: '12px 16px', borderRadius: '6px',
              background: '#1a1a24', border: '1px dashed var(--accent-color)', textAlign: 'center'
            }}>
              <div style={{ color: 'var(--accent-color)', fontSize: '9px', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>Win Rate</div>
              <div style={{ color: '#eee', fontSize: '20px', fontWeight: 'bold' }}>
                {stats.matchesPlayed > 0 
                  ? `${Math.round((stats.matchesWon / stats.matchesPlayed) * 100)}%`
                  : '0%'}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Decks section */}
      <section style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
          <h2 style={{ color: 'var(--accent-color)', margin: 0 }}>Your Decks</h2>
          <button
            onClick={() => setEditorState({ open: true, mode: 'create' })}
            disabled={decks.length >= MAX_DECKS}
            title={decks.length >= MAX_DECKS
              ? `Deck limit reached (${MAX_DECKS}). Delete one to create a new deck.`
              : 'Create a new deck'}
            style={{
              background: 'var(--accent-color)',
              border: 'none',
              color: '#000',
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: 'bold',
              cursor: decks.length >= MAX_DECKS ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              opacity: decks.length >= MAX_DECKS ? 0.5 : 1,
            }}
          >+ New deck</button>
        </div>
        <p style={{ color: '#888', fontSize: '13px', marginTop: 0, marginBottom: '20px' }}>
          {decks.length === 0
            ? "You don't have any decks yet. Create one to start playing."
            : `${decks.length}/${MAX_DECKS} decks. Cards can belong to multiple decks.`}
        </p>
        {deckActionError && (
          <div style={{ color: '#e57373', fontSize: '12px', marginBottom: '10px' }}>{deckActionError}</div>
        )}
        {decks.length > 0 && (
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            {decks.map(deck => {
              const handCount = deck.cards.filter(c => c.card.type === 'Hand').length;
              const missionCount = deck.cards.filter(c => c.card.type === 'Mission').length;
              const suitsInDeck = Array.from(new Set(deck.cards.map(c => c.card.suit)));
              const isPending = pendingDeckId === deck.id;
              return (
                <div
                  key={deck.id}
                  style={{
                    background: '#1a1a24', border: '2px solid #444',
                    borderRadius: '6px', padding: '12px 16px', minWidth: '240px',
                    opacity: isPending ? 0.6 : 1,
                  }}
                >
                  <div style={{ color: '#ffb703', fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                    {deck.name}
                  </div>
                  <div style={{ color: '#aaa', fontSize: '11px', marginBottom: '6px' }}>
                    {deck.cards.length} cards ({handCount} hand + {missionCount} missions)
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {suitsInDeck.map(s => (
                      <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <span
                          className={`suit-icon suit-icon-${s.replace(/\s+/g, '-')}`}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span style={{ color: '#ddd', fontSize: '10px', textTransform: 'capitalize' }}>{s}</span>
                      </span>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => setEditorState({ open: true, mode: 'edit', deck })}
                      disabled={isPending}
                      style={deckBtnStyle('#1a2a3a', 'var(--accent-color)')}
                    >Edit</button>
                    <button
                      onClick={() => duplicateDeck(deck)}
                      disabled={isPending}
                      style={deckBtnStyle('#1a1a24', '#aaa')}
                    >Duplicate</button>
                    <button
                      onClick={() => deleteDeck(deck)}
                      disabled={isPending}
                      style={deckBtnStyle('#2a1010', '#e57373')}
                    >Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Deck Editor modal */}
      {editorState.open && (
        <DeckEditor
          mode={editorState.mode}
          existingDeck={editorState.mode === 'edit' ? editorState.deck : undefined}
          cards={cards}
          token={token}
          onClose={() => setEditorState({ open: false })}
          onSaved={async () => {
            setEditorState({ open: false });
            await refreshDecks();
          }}
        />
      )}

      {/* Collection section */}
      <section>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
          <h2 style={{ color: 'var(--accent-color)', margin: 0 }}>Your Cards</h2>
          {/* ⚠️ DEV ONLY — visibile solo in `npm run dev` (il server risponde 403 in prod). */}
          {import.meta.env.DEV && (
          <button
            onClick={grantAllCards}
            disabled={grantingAll}
            title="DEV ONLY — grants a full set of all 8 suits. Will be removed at release."
            style={{
              background: '#3a2a00', border: '1px dashed #ffb703', color: '#ffb703',
              padding: '6px 12px', borderRadius: '6px', fontSize: '12px',
              cursor: grantingAll ? 'wait' : 'pointer', fontFamily: 'inherit',
            }}
          >🧪 {grantingAll ? 'Adding…' : 'Add all cards (dev)'}</button>
          )}
        </div>
        <p style={{ color: '#888', fontSize: '13px', marginTop: 0, marginBottom: '24px' }}>
          {cards.length} cards total. Win matches to add more (coming soon).
        </p>

        {SUIT_ORDER.map(suit => {
          const arr = bySuit.get(suit);
          if (!arr || arr.length === 0) return null;
          return (
            <div key={suit} style={{ marginBottom: '32px' }}>
              <h3 style={{ color: '#ffb703', fontSize: '14px', textTransform: 'capitalize', marginBottom: '12px' }}>
                {suit} <span style={{ color: '#666', fontSize: '12px' }}>({arr.length})</span>
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {arr.map(r => {
                  const isNew = showHighlights && newLoot?.cardIds?.includes(r.id);
                  return (
                    <div
                      key={r.id}
                      title={`Acquired: ${new Date(r.acquiredAt).toLocaleDateString()} (${r.source})`}
                      style={{ position: 'relative' }}
                    >
                      <div className={isNew ? 'new-loot-glow-container' : ''}>
                        <Card card={r.card} staticContext />
                      </div>
                      {isNew && (
                        <div style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          background: '#39ff14',
                          color: '#000',
                          fontSize: '8px',
                          fontWeight: 'bold',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          boxShadow: '0 0 6px #39ff14',
                          zIndex: 5,
                          fontFamily: 'inherit',
                          animation: 'pulse-badge 1.5s infinite alternate'
                        }}>
                          NEW
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}

// Lifetime resource counts earned via run extracts. Renders a pill per suit
// with the spritesheet icon and the number. Suits with 0 count are hidden so
// a brand-new account doesn't see 8 empty pills; once a player extracts even
// once for a given suit, that pill appears and stays.
function Stash({ bankedResources, newLoot }: { bankedResources: Record<string, number>; newLoot: { resources: Record<string, number> } | null }) {
  const entries = SUIT_ORDER
    .map(s => [s, bankedResources[s] ?? 0] as const)
    .filter(([, n]) => n > 0);

  return (
    <section style={{ marginBottom: '40px' }}>
      <h2 style={{ color: 'var(--accent-color)', marginTop: 0, marginBottom: '4px' }}>Your Stash</h2>
      <p style={{ color: '#888', fontSize: '13px', marginTop: 0, marginBottom: '12px' }}>
        Lifetime resources banked from successful run extracts.
        {entries.length === 0 && ' Extract a run to start filling this up.'}
      </p>
      {entries.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {entries.map(([suit, count]) => {
            const increment = newLoot?.resources?.[suit] ?? 0;
            const isIncrement = increment > 0;
            return (
              <div
                key={suit}
                title={`${count} ${suit}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '8px 14px', borderRadius: '6px',
                  background: '#15151f', border: '1px solid #2a2a3a',
                  minWidth: '110px',
                }}
              >
                <div className={`suit-icon suit-icon-${suit.replace(/\s+/g, '-')}`} style={{ width: 28, height: 28 }} />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <div style={{ color: '#eee', fontSize: 16, fontWeight: 'bold', lineHeight: 1 }}>{count}</div>
                    {isIncrement && (
                      <div style={{
                        color: '#39ff14',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        textShadow: '0 0 4px #39ff14',
                        animation: 'pulse-text 1.5s infinite alternate'
                      }}>
                        ↑+{increment}
                      </div>
                    )}
                  </div>
                  <div style={{ color: '#888', fontSize: 9, textTransform: 'capitalize', letterSpacing: 0.3, marginTop: 2 }}>{suit}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
